import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ApiUser } from '../../types';
import api from '../../api';
import { cookieUtils } from '../../utils/cookies';

// User state interface
interface UserState {
  user: ApiUser | null;
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

// Initial state
const initialState: UserState = {
  user: null,
  isLoading: false,
  error: null,
  isAuthenticated: false,
};

// Async thunks
export const fetchUserProfile = createAsyncThunk(
  'user/fetchProfile',
  async (_, { rejectWithValue }) => {
    try {
      const userData = await api.auth.getMe();
      return userData;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch user profile'
      );
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  'user/updateProfile',
  async (data: Partial<ApiUser>, { rejectWithValue }) => {
    try {
      const updatedUser = await api.user.updateProfile(data);
      return updatedUser;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to update profile'
      );
    }
  }
);

export const loginUser = createAsyncThunk(
  'user/login',
  async (authResponse: { user: ApiUser; accessToken: string; rememberMe?: boolean }, { rejectWithValue }) => {
    try {
      // Store token in cookies
      cookieUtils.setAccessToken(authResponse.accessToken, authResponse.rememberMe);
      // Store user data in cookies
      cookieUtils.setUser(authResponse.user, authResponse.rememberMe);
      return authResponse.user;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to login'
      );
    }
  }
);

export const logoutUser = createAsyncThunk(
  'user/logout',
  async (_, { rejectWithValue }) => {
    try {
      await api.auth.logout();
      // Clear tokens and user data
      cookieUtils.clearTokens();
      cookieUtils.removeUser();

      // Clear remember me data
      localStorage.removeItem('remembered_email');
      localStorage.removeItem('login_remember_me');

      return null;
    } catch (error: any) {
      // Even if logout API fails, clear local data
      cookieUtils.clearTokens();
      cookieUtils.removeUser();

      // Clear remember me data
      localStorage.removeItem('remembered_email');
      localStorage.removeItem('login_remember_me');

      return null;
    }
  }
);

// User slice
const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<ApiUser | null>) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
    },
    clearUser: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    initializeUser: (state) => {
      // Initialize user from cookies on app load
      const userFromCookie = cookieUtils.getUser();
      const token = cookieUtils.getAccessToken();
      if (userFromCookie && token) {
        state.user = userFromCookie;
        state.isAuthenticated = true;
      }
    },
  },
  extraReducers: (builder) => {
    // Fetch user profile
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        // Update user in cookies, respecting remember me preference
        const rememberMe = localStorage.getItem('login_remember_me') !== 'false';
        cookieUtils.setUser(action.payload, rememberMe);
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update user profile
    builder
      .addCase(updateUserProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        // Update user in cookies, respecting remember me preference
        const rememberMe = localStorage.getItem('login_remember_me') !== 'false';
        cookieUtils.setUser(action.payload, rememberMe);
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Login user
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.isAuthenticated = false;
      });

    // Logout user
    builder
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logoutUser.rejected, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      });
  },
});

export const { setUser, clearUser, setError, initializeUser } = userSlice.actions;
export default userSlice.reducer;

